import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate, useParams } from "@tanstack/react-router"
import { useMemo } from "react"
import {
  type CompanyProfileDTO,
  type CompanyProfileRevisionDTO,
  CompanyProfileService,
} from "../../api/companyProfile"

type RevisionDiffRow = {
  label: string
  previousValue: string
  currentValue: string
}

export default function CompanyProfileRevisionPage() {
  const { revisionId } = useParams({
    from: "/_layout/company-settings/revisions/$revisionId",
  })
  const navigate = useNavigate()
  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["companyProfile", "revisions", revisionId],
    queryFn: () => CompanyProfileService.getRevisionById(revisionId),
    retry: 1,
  })
  const { data: revisions = [] } = useQuery<CompanyProfileRevisionDTO[]>({
    queryKey: ["companyProfile", "revisions"],
    queryFn: () => CompanyProfileService.getRevisions(),
    retry: 1,
  })

  const previousRevision = useMemo(() => {
    return revisions.find(
      (revision) => revision.revisionNumber === (data?.revisionNumber ?? 0) - 1,
    )
  }, [data?.revisionNumber, revisions])
  const nextRevision = useMemo(() => {
    return revisions.find(
      (revision) => revision.revisionNumber === (data?.revisionNumber ?? 0) + 1,
    )
  }, [data?.revisionNumber, revisions])

  const diffRows = useMemo<RevisionDiffRow[]>(() => {
    if (!data || !previousRevision) return []

    const snapshot = data.snapshot
    const previous = previousRevision.snapshot
    const rows: RevisionDiffRow[] = []

    const register = (
      label: string,
      currentValue: string,
      previousValue: string,
    ) => {
      if ((currentValue || "") === (previousValue || "")) return
      rows.push({
        label,
        previousValue: previousValue || "—",
        currentValue: currentValue || "—",
      })
    }

    const formatAddress = (address: CompanyProfileDTO["address"]) =>
      [
        address.line1,
        address.line2,
        address.postalCode,
        address.city,
        address.countryCode,
      ]
        .filter(Boolean)
        .join(", ")

    register("Krótka nazwa", snapshot.shortName, previous.shortName)
    register("Nazwa prawna", snapshot.legalName, previous.legalName)
    register("Nazwa WMS", snapshot.wmsName, previous.wmsName)
    register("NIP / Tax ID", snapshot.taxId, previous.taxId)
    register("REGON", snapshot.regon, previous.regon)
    register("KRS", snapshot.krs, previous.krs)
    register("BDO", snapshot.bdo, previous.bdo)
    register("E-mail", snapshot.email, previous.email)
    register("Telefon", snapshot.phone, previous.phone)
    register("WWW", snapshot.website, previous.website)
    register(
      "Adres",
      formatAddress(snapshot.address),
      formatAddress(previous.address),
    )
    register("Locale", snapshot.locale, previous.locale)
    register("Timezone", snapshot.timezone, previous.timezone)
    register("Login logo", snapshot.assets.loginLogo, previous.assets.loginLogo)
    register("Label logo", snapshot.assets.labelLogo, previous.assets.labelLogo)
    register("Login alt", snapshot.alt.loginLogo, previous.alt.loginLogo)
    register("Label alt", snapshot.alt.labelLogo, previous.alt.labelLogo)

    return rows
  }, [data, previousRevision])

  if (isPending) {
    return (
      <Flex justify="center" align="center" w="full" h="60vh">
        <Spinner size="xl" color="main.accent" />
      </Flex>
    )
  }

  if (isError || !data) {
    return (
      <Container maxW="5xl" py={6}>
        <Stack spacing={4}>
          <Button
            alignSelf="start"
            variant="outline"
            h="auto"
            minH={10}
            px={4}
            py={2}
            whiteSpace="normal"
            textAlign="center"
            onClick={() => navigate({ to: "/company-settings" })}
          >
            Wróć do profilu firmy
          </Button>
          <Text color="red.500">
            Nie udało się pobrać rewizji profilu firmy:{" "}
            {(error as any)?.message ?? "Błąd"}
          </Text>
        </Stack>
      </Container>
    )
  }

  return (
    <Container maxW="5xl" py={6}>
      <Stack spacing={4}>
        <Button
          alignSelf="start"
          variant="outline"
          h="auto"
          minH={10}
          px={4}
          py={2}
          whiteSpace="normal"
          textAlign="center"
          onClick={() => navigate({ to: "/company-settings" })}
        >
          Wróć do profilu firmy
        </Button>

        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          gap={3}
        >
          <Button
            variant="outline"
            isDisabled={!previousRevision}
            h="auto"
            minH={10}
            px={4}
            py={2}
            whiteSpace="normal"
            textAlign="center"
            onClick={() => {
              if (!previousRevision) return
              navigate({
                to: "/company-settings/revisions/$revisionId",
                params: { revisionId: previousRevision.id },
              })
            }}
          >
            Poprzednia rewizja
          </Button>
          <Button
            variant="outline"
            isDisabled={!nextRevision}
            h="auto"
            minH={10}
            px={4}
            py={2}
            whiteSpace="normal"
            textAlign="center"
            onClick={() => {
              if (!nextRevision) return
              navigate({
                to: "/company-settings/revisions/$revisionId",
                params: { revisionId: nextRevision.id },
              })
            }}
          >
            Następna rewizja
          </Button>
        </Flex>

        <Box
          bgGradient="linear(to-r, teal.600, green.600)"
          color="white"
          borderRadius="2xl"
          px={{ base: 5, md: 8 }}
          py={{ base: 5, md: 7 }}
          boxShadow="lg"
        >
          <Stack spacing={2}>
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              gap={3}
            >
              <Box>
                <Heading size="lg">Rewizja profilu firmy</Heading>
                <Text mt={1} opacity={0.95}>
                  Szczegółowy, niezmienny snapshot danych firmy dla rewizji{" "}
                  {data.revisionNumber}.
                </Text>
              </Box>
              <Badge
                bg="whiteAlpha.250"
                color="white"
                borderRadius="full"
                px={3}
                py={1}
              >
                Rev. {data.revisionNumber}
              </Badge>
            </Flex>
            <Text opacity={0.95}>
              {new Date(data.createdAt).toLocaleString()} •{" "}
              {data.createdBy.fullName || "System"}
              {data.createdBy.acronym ? ` (${data.createdBy.acronym})` : ""}
            </Text>
          </Stack>
        </Box>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          p={4}
        >
          <Stack spacing={4}>
            <Box>
              <Heading size="md">Metadane zmiany</Heading>
              <Text mt={1} color="gray.500">
                Powód zapisany przy utworzeniu rewizji.
              </Text>
            </Box>
            <Text>{data.changeReason || "Brak opisu zmiany"}</Text>
          </Stack>
        </Box>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          p={4}
        >
          <Stack spacing={4}>
            <Box>
              <Heading size="md">Różnice względem poprzedniej rewizji</Heading>
              <Text mt={1} color="gray.500">
                Zmiany wykryte między aktualnie otwartą rewizją a poprzednim
                snapshotem.
              </Text>
            </Box>

            {!previousRevision ? (
              <Text color="gray.500">
                To jest pierwsza rewizja profilu firmy. Nie ma wcześniejszej
                wersji do porównania.
              </Text>
            ) : diffRows.length === 0 ? (
              <Text color="gray.500">
                Nie wykryto zmian w polach porównywanych z poprzednią rewizją.
              </Text>
            ) : (
              <Stack spacing={3}>
                {diffRows.map((row) => (
                  <Box
                    key={row.label}
                    borderWidth="1px"
                    borderColor={panelBorder}
                    borderRadius="xl"
                    p={3}
                  >
                    <Stack spacing={2}>
                      <Text fontWeight="semibold">{row.label}</Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                        <Box>
                          <Text fontSize="sm" color="gray.500">
                            Poprzednia wartość
                          </Text>
                          <Text>{row.previousValue}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="sm" color="gray.500">
                            Bieżąca wartość
                          </Text>
                          <Text>{row.currentValue}</Text>
                        </Box>
                      </SimpleGrid>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </Box>

        <Box
          bg={panelBg}
          borderWidth="1px"
          borderColor={panelBorder}
          borderRadius="2xl"
          p={4}
        >
          <Stack spacing={4}>
            <Heading size="md">Snapshot danych firmy</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Krótka nazwa
                </Text>
                <Text>{data.snapshot.shortName || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Nazwa prawna
                </Text>
                <Text>{data.snapshot.legalName || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Nazwa WMS
                </Text>
                <Text>{data.snapshot.wmsName || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  NIP / Tax ID
                </Text>
                <Text>{data.snapshot.taxId || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  REGON
                </Text>
                <Text>{data.snapshot.regon || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  KRS
                </Text>
                <Text>{data.snapshot.krs || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  BDO
                </Text>
                <Text>{data.snapshot.bdo || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  E-mail
                </Text>
                <Text>{data.snapshot.email || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Telefon
                </Text>
                <Text>{data.snapshot.phone || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  WWW
                </Text>
                <Text>{data.snapshot.website || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Locale / Timezone
                </Text>
                <Text>
                  {data.snapshot.locale || "—"} •{" "}
                  {data.snapshot.timezone || "—"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Adres
                </Text>
                <Text>
                  {[
                    data.snapshot.address.line1,
                    data.snapshot.address.line2,
                    data.snapshot.address.postalCode,
                    data.snapshot.address.city,
                    data.snapshot.address.countryCode,
                  ]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Branding assets
                </Text>
                <Text>
                  {data.snapshot.assets.loginLogo || "Brak login logo"} {" • "}
                  {data.snapshot.assets.labelLogo || "Brak label logo"}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Alt teksty
                </Text>
                <Text>
                  {data.snapshot.alt.loginLogo || "Brak login alt"} {" • "}
                  {data.snapshot.alt.labelLogo || "Brak label alt"}
                </Text>
              </Box>
            </SimpleGrid>
          </Stack>
        </Box>
      </Stack>
    </Container>
  )
}
